/* ============================================================================
   PARHELIK — collection controller (filtering / sorting / paginate / view)
   Vanilla, dependency-free, idempotent. Mirrors minimog's collection.js +
   storefont-filters.js + tags-filters.js, restyled to the Parhelik contract.

   Scoped per [data-ph-collection] root. For each collection section it:
     • on [data-ph-facet-form] change (checkbox / radio / price range) or
       [data-ph-sort] change -> assembles query params from the form, fetches
       the current collection URL with ?section_id=<id>, parses the returned
       HTML and replaces, in place:
         - [data-ph-product-grid]   (the grid container)
         - [data-ph-active-filters] (removable .ph-pill chips)
         - [data-ph-results-count]  (result count)
       then history.pushState the user-facing URL (without section_id).
     • [data-ph-load-more] click -> fetch the next page, append the product
       cards to the grid, update / hide the button when no next page.
     • [data-ph-view] click -> set data-cols on the grid (column switch).
     • price-range inputs are debounced; chip removal + [data-ph-clear-all]
       navigate via the Shopify url_to_remove on the chip / clear link.
     • loading state toggled on the grid ([data-ph-loading]).
     • popstate re-renders from the URL.

   Public API: window.PHCollection.init(), .refresh()
   Init on DOMContentLoaded + shopify:section:load. node --check clean.
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHCollection && window.PHCollection._installed) return;

  /* ---- selectors (the COLLECTION JS CONTRACT data-ph-* hooks) ---- */
  var SEL = {
    root: '[data-ph-collection]',
    form: '[data-ph-facet-form]',
    facet: '[data-ph-facet]',
    sort: '[data-ph-sort]',
    grid: '[data-ph-product-grid]',
    count: '[data-ph-results-count]',
    activeFilters: '[data-ph-active-filters]',
    clearAll: '[data-ph-clear-all]',
    loadMore: '[data-ph-load-more]',
    pagination: '[data-ph-pagination]',
    view: '[data-ph-view]',
    chipRemove: '[data-ph-remove-filter]',
    productItem: '[data-ph-product]'
  };

  var DEBOUNCE_MS = 450;
  var INSTANCES = [];

  /* ---------------------------------------------------------------- utils */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function closestSection(el) {
    // Theme-editor wrapper id is shopify-section-<id>; fall back to data attr.
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.id && node.id.indexOf('shopify-section-') === 0) {
        return node.id.replace('shopify-section-', '');
      }
      node = node.parentNode;
    }
    return null;
  }

  function getSectionId(root) {
    return (
      root.getAttribute('data-section-id') ||
      root.dataset.sectionId ||
      closestSection(root) ||
      ''
    );
  }

  /* Build the user-facing search string from the facet form + sort select. */
  function buildSearchParams(inst) {
    var params = new URLSearchParams();

    if (inst.form) {
      var data = new FormData(inst.form);
      data.forEach(function (value, key) {
        if (value === '' || value == null) return;
        params.append(key, value);
      });
    }

    // The sort <select> may live outside the form; include it explicitly.
    if (inst.sort && inst.sort.value && !params.has('sort_by')) {
      params.set('sort_by', inst.sort.value);
    }

    return params.toString();
  }

  /* -------------------------------------------------------------- fetching */
  function fetchSection(inst, search) {
    var base = inst.collectionUrl || window.location.pathname;
    var url = base + '?section_id=' + encodeURIComponent(inst.sectionId);
    if (search) url += '&' + search;
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Parhelik collection: section fetch failed (' + res.status + ')');
      return res.text();
    });
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  /* ---- swap the grid + active-filter chips + count from fetched markup ---- */
  function applyRender(inst, html) {
    var doc = parseHTML(html);

    var newGrid = doc.querySelector(SEL.grid);
    var newFilters = doc.querySelector(SEL.activeFilters);
    var newCount = doc.querySelector(SEL.count);

    if (newGrid && inst.grid) {
      // preserve current column view across re-render
      var cols = inst.grid.getAttribute('data-cols');
      if (cols) newGrid.setAttribute('data-cols', cols);
      inst.grid.replaceWith(newGrid);
      inst.grid = newGrid;
    }

    if (newFilters && inst.activeFilters) {
      inst.activeFilters.replaceWith(newFilters);
      inst.activeFilters = newFilters;
    } else if (inst.activeFilters) {
      // no chips in new markup -> empty the container
      inst.activeFilters.innerHTML = newFilters ? newFilters.innerHTML : '';
    }

    if (newCount && inst.count) {
      inst.count.replaceWith(newCount);
      inst.count = newCount;
    }

    // The filter form's facet inputs / counts also change with the result set;
    // refresh the form contents in place so the next FormData is accurate.
    var newForm = doc.querySelector(SEL.form);
    if (newForm && inst.form) {
      inst.form.innerHTML = newForm.innerHTML;
    }

    // re-resolve pagination + load-more for the new markup
    inst.pagination = $(SEL.pagination, inst.root) || inst.pagination;
    inst.loadMore = $(SEL.loadMore, inst.root);
    inst.currentPage = 1;
    readPaginationData(inst);

    // let other Parhelik controllers (cards / reveal) wire the new nodes
    document.dispatchEvent(new CustomEvent('ph:collection:rendered', { detail: { root: inst.root } }));
    if (window.PHProduct && typeof window.PHProduct.init === 'function') window.PHProduct.init();
  }

  function readPaginationData(inst) {
    var src = inst.loadMore || inst.pagination || inst.grid;
    if (!src) { inst.totalPages = 1; return; }
    inst.totalPages = Number(src.getAttribute('data-total-pages')) || 1;
    var cur = Number(src.getAttribute('data-current-page'));
    if (cur) inst.currentPage = cur;
  }

  function setLoading(inst, loading) {
    if (!inst.grid) return;
    if (loading) {
      inst.grid.setAttribute('data-ph-loading', '');
      inst.grid.setAttribute('aria-busy', 'true');
    } else {
      inst.grid.removeAttribute('data-ph-loading');
      inst.grid.removeAttribute('aria-busy');
    }
  }

  /* -------------------------------------------------------------- render */
  function render(inst, search, push) {
    if (inst.busy) return;
    inst.busy = true;
    setLoading(inst, true);

    fetchSection(inst, search)
      .then(function (html) {
        applyRender(inst, html);
        if (push) {
          var userUrl = (inst.collectionUrl || window.location.pathname) + (search ? '?' + search : '');
          history.pushState({ phSearch: search }, '', userUrl);
        }
      })
      .catch(function (err) { if (window.console) console.error(err); })
      .finally(function () {
        inst.busy = false;
        setLoading(inst, false);
      });
  }

  function submitFromForm(inst) {
    render(inst, buildSearchParams(inst), true);
  }

  /* ------------------------------------------------------------ load more */
  function loadMore(inst) {
    if (inst.busy) return;
    if (!inst.loadMore) return;
    var next = inst.currentPage + 1;
    if (next > inst.totalPages) { hideLoadMore(inst); return; }

    inst.busy = true;
    inst.loadMore.classList.add('is-loading');
    inst.loadMore.setAttribute('aria-busy', 'true');

    var search = buildSearchParams(inst);
    var pageParam = (search ? search + '&' : '') + 'page=' + next;

    fetchSection(inst, pageParam)
      .then(function (html) {
        var doc = parseHTML(html);
        var newGrid = doc.querySelector(SEL.grid);
        if (newGrid && inst.grid) {
          $all(SEL.productItem, newGrid).forEach(function (item) {
            inst.grid.appendChild(item);
          });
        }
        inst.currentPage = next;
        document.dispatchEvent(new CustomEvent('ph:collection:appended', { detail: { root: inst.root } }));
        if (window.PHProduct && typeof window.PHProduct.init === 'function') window.PHProduct.init();
        if (inst.currentPage >= inst.totalPages) hideLoadMore(inst);
      })
      .catch(function (err) { if (window.console) console.error(err); })
      .finally(function () {
        inst.busy = false;
        if (inst.loadMore) {
          inst.loadMore.classList.remove('is-loading');
          inst.loadMore.removeAttribute('aria-busy');
        }
      });
  }

  function hideLoadMore(inst) {
    if (!inst.loadMore) return;
    var wrap = inst.loadMore.closest(SEL.pagination) || inst.loadMore;
    wrap.setAttribute('hidden', '');
    if (inst.observer) {
      inst.observer.disconnect();
      inst.observer = null;
    }
  }

  /* ----------------------------------------------------------- view cols */
  function setView(inst, btn) {
    var cols = btn.getAttribute('data-ph-view') || btn.getAttribute('data-cols');
    if (!cols || !inst.grid) return;
    inst.grid.setAttribute('data-cols', cols);
    $all(SEL.view, inst.root).forEach(function (b) {
      var on = (b.getAttribute('data-ph-view') || b.getAttribute('data-cols')) === cols;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    try { window.localStorage.setItem('phGridCols', cols); } catch (e) { /* private mode */ }
  }

  function restoreView(inst) {
    var cols;
    try { cols = window.localStorage.getItem('phGridCols'); } catch (e) { cols = null; }
    if (!cols || !inst.grid) return;
    // only restore if a matching view button exists
    var match = $all(SEL.view, inst.root).some(function (b) {
      return (b.getAttribute('data-ph-view') || b.getAttribute('data-cols')) === cols;
    });
    if (match) {
      inst.grid.setAttribute('data-cols', cols);
      $all(SEL.view, inst.root).forEach(function (b) {
        var on = (b.getAttribute('data-ph-view') || b.getAttribute('data-cols')) === cols;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  }

  /* ----------------------------------------- chip remove / clear-all nav */
  function navigateTo(inst, url) {
    if (!url) return;
    // url_to_add / url_to_remove are full collection URLs; render from their query.
    var a = document.createElement('a');
    a.href = url;
    inst.collectionUrl = a.pathname;
    var search = a.search.replace(/^\?/, '');
    render(inst, search, true);
  }

  function handleActiveFiltersClick(inst, e) {
    var chip = e.target.closest(SEL.chipRemove + ', a');
    if (!chip || !inst.activeFilters.contains(chip)) return;
    var url = chip.getAttribute('data-href') || chip.getAttribute('href');
    if (url) {
      e.preventDefault();
      navigateTo(inst, url);
    }
  }

  function handleClearAll(inst, link) {
    var url = link.getAttribute('data-href') || link.getAttribute('href');
    if (url && url !== '#') {
      navigateTo(inst, url);
    } else {
      // fall back to the collection base with no params
      inst.collectionUrl = inst.collectionUrl || window.location.pathname;
      render(inst, '', true);
    }
  }

  /* ----------------------------------------------------- infinite scroll */
  function initInfinite(inst) {
    if (!inst.loadMore) return;
    if (inst.loadMore.getAttribute('data-ph-load-more') !== 'infinite') return;
    if (!('IntersectionObserver' in window)) return;
    inst.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) loadMore(inst);
      });
    }, { rootMargin: '400px 0px' });
    inst.observer.observe(inst.loadMore);
  }

  /* ------------------------------------------------------- instance wiring */
  function makeInstance(root) {
    var inst = {
      root: root,
      form: $(SEL.form, root),
      sort: $(SEL.sort, root),
      grid: $(SEL.grid, root),
      count: $(SEL.count, root),
      activeFilters: $(SEL.activeFilters, root),
      clearAll: $(SEL.clearAll, root),
      loadMore: $(SEL.loadMore, root),
      pagination: $(SEL.pagination, root),
      sectionId: getSectionId(root),
      collectionUrl: root.getAttribute('data-collection-url') || window.location.pathname,
      currentPage: 1,
      totalPages: 1,
      busy: false,
      observer: null,
      handlers: []
    };
    readPaginationData(inst);
    return inst;
  }

  function bind(inst) {
    // facet form: debounce price ranges, immediate for everything else.
    if (inst.form) {
      var debounced = debounce(function () { submitFromForm(inst); }, DEBOUNCE_MS);
      var onChange = function (e) {
        var t = e.target;
        if (!t.matches(SEL.facet) && !t.closest(SEL.facet)) return;
        var type = (t.getAttribute('type') || '').toLowerCase();
        var isRange = type === 'range' || type === 'number' || t.hasAttribute('data-ph-price');
        if (isRange) debounced();
        else submitFromForm(inst);
      };
      // `input` fires live for ranges/numbers; `change` for checkbox/radio.
      inst.form.addEventListener('input', function (e) {
        var t = e.target;
        var type = (t.getAttribute('type') || '').toLowerCase();
        if (type === 'range' || type === 'number' || t.hasAttribute('data-ph-price')) onChange(e);
      });
      inst.form.addEventListener('change', function (e) {
        var t = e.target;
        var type = (t.getAttribute('type') || '').toLowerCase();
        if (type === 'range' || type === 'number' || t.hasAttribute('data-ph-price')) return;
        onChange(e);
      });
      // prevent native full-page submit
      inst.form.addEventListener('submit', function (e) { e.preventDefault(); submitFromForm(inst); });
    }

    // sort select (may be outside the form)
    if (inst.sort) {
      inst.sort.addEventListener('change', function () { submitFromForm(inst); });
    }

    // active filter chips (delegated; container is replaced on render)
    if (inst.root) {
      inst.root.addEventListener('click', function (e) {
        // active-filter chip removal
        var af = e.target.closest(SEL.activeFilters);
        if (af) { handleActiveFiltersClick(inst, e); return; }
        // clear-all
        var clear = e.target.closest(SEL.clearAll);
        if (clear) { e.preventDefault(); handleClearAll(inst, clear); return; }
        // load-more button
        var lm = e.target.closest(SEL.loadMore);
        if (lm) { e.preventDefault(); loadMore(inst); return; }
        // view toggle
        var view = e.target.closest(SEL.view);
        if (view) { e.preventDefault(); setView(inst, view); return; }
      });
    }

    // browser back / forward
    inst.onPop = function () {
      var search = window.location.search.replace(/^\?/, '');
      inst.collectionUrl = window.location.pathname;
      render(inst, search, false);
    };
    window.addEventListener('popstate', inst.onPop);

    initInfinite(inst);
    restoreView(inst);
  }

  function unbind(inst) {
    if (inst.onPop) window.removeEventListener('popstate', inst.onPop);
    if (inst.observer) { inst.observer.disconnect(); inst.observer = null; }
  }

  /* ----------------------------------------------------------------- API */
  function initRoot(root) {
    if (!root || root.dataset.phCollectionReady === '1') return;
    root.dataset.phCollectionReady = '1';
    var inst = makeInstance(root);
    bind(inst);
    INSTANCES.push(inst);
  }

  function init(scope) {
    var ctx = scope || document;
    $all(SEL.root, ctx).forEach(initRoot);
    // if the scope itself is a root
    if (ctx.nodeType === 1 && ctx.matches && ctx.matches(SEL.root)) initRoot(ctx);
  }

  function teardown(root) {
    for (var i = INSTANCES.length - 1; i >= 0; i--) {
      if (!root || INSTANCES[i].root === root || !document.contains(INSTANCES[i].root)) {
        unbind(INSTANCES[i]);
        INSTANCES.splice(i, 1);
      }
    }
  }

  window.PHCollection = {
    _installed: true,
    init: init,
    refresh: function () { teardown(); init(); },
    instances: INSTANCES
  };

  /* --------------------------------------------------------- bootstrapping */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify theme editor: re-init the loaded section, tear down removed ones.
  document.addEventListener('shopify:section:load', function (e) {
    teardown();
    init(e.target || document);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector(SEL.root) : null;
    teardown(root);
  });
})();
